/**
 * Leaving the world from the game menu (stream note 47, lane AT 2026-10-09):
 * Character select and Log out.
 *
 * Both leave the room with a consented leave (Colyseus close 4000) before the
 * page moves on. The server treats that like a kick, not a dropped socket: it
 * saves the hero and removes it at once, so other players never see it stand
 * idle through the 20-45 s reconnect grace a page unload (1001) would hold
 * open (PlayerLifecycleService/leave.ts). `markNavigating` keeps the client
 * from flashing "Connection Lost" or reconnecting to the room it just left,
 * and the leaving latch stops `useServerConnection` from re-joining while the
 * next page loads.
 *
 * Character select lands on `/resume`, the account roster a fresh visit to
 * helix2000.com lands on (applyMainGameRedirect). Log out signs out (local
 * token, and the Twitch cookie through its logout route) and lands on `/`,
 * the title. A guest's hero is session-only (D27): it has no roster to return
 * to, so the menu offers only "End guest session", which is Log out.
 */
import { ServiceRegistry } from '../core/ServiceRegistry';
import { storage } from './StorageService';
import { isGuestJwt } from './authToken';
import { gameLogger } from '../../utils/GameLogger';

export type WorldExit = 'characterSelect' | 'logOut';

/** The account roster: where a fresh visit lands once signed in. */
export const CHARACTER_SELECT_PATH = '/resume';
/** The title: sign in, or play as a guest. */
export const TITLE_PATH = '/';
/** Leave and sign-out are courtesies: never strand the player behind a slow server. */
export const WORLD_EXIT_TIMEOUT_MS = 2000;

interface ExitRoom { leave(consented?: boolean): Promise<unknown> }
interface ExitNetwork {
  markNavigating?: () => void;
  getRoom?: () => ExitRoom | null;
}

export interface WorldExitDeps {
  network: () => ExitNetwork | null;
  navigate: (path: string) => void;
  signOutTwitch: () => Promise<unknown>;
  timeoutMs: number;
}

const defaultDeps = (): WorldExitDeps => ({
  network: () => (ServiceRegistry.getInstance().get('NetworkManager', { optional: true }) as ExitNetwork | null)
    ?? ((globalThis as { nm?: ExitNetwork }).nm ?? null),
  navigate: path => window.location.assign(path),
  signOutTwitch: () => fetch('/api/auth/twitch/logout', { method: 'POST', credentials: 'same-origin' }),
  timeoutMs: WORLD_EXIT_TIMEOUT_MS,
});

let leaving = false;

/** True once the player chose to leave: the page is on its way out; do not re-join. */
export function isLeavingWorld(): boolean { return leaving; }
/** Tests only. */
export function _testOnly_resetWorldExit(): void { leaving = false; }

/** True when this page plays a guest's session-only hero (D27). */
export function isGuestSession(): boolean {
  try { return isGuestJwt(storage.get('helix_jwt')); } catch { return false; }
}

function settle(work: Promise<unknown>, ms: number): Promise<void> {
  return Promise.race([
    work.then(() => undefined, () => undefined),
    new Promise<void>(resolve => setTimeout(resolve, ms)),
  ]);
}

/** Leave the room cleanly, then go to the roster (characterSelect) or the title (logOut). */
export async function leaveWorld(exit: WorldExit, deps: WorldExitDeps = defaultDeps()): Promise<void> {
  if (leaving) return;
  leaving = true;
  gameLogger.info('auth', `🚪 Leaving the world: ${exit}`);
  const network = deps.network();
  network?.markNavigating?.();
  const room = network?.getRoom?.() ?? null;
  if (room) await settle(room.leave(true), deps.timeoutMs);

  if (exit === 'logOut') {
    const guest = isGuestSession();
    for (const key of ['helix_jwt', 'helix_last_character', 'helix_twitch_recent'] as const) {
      try { storage.remove(key); } catch { /* storage disabled: the next boot probes the server anyway */ }
    }
    if (!guest) await settle(deps.signOutTwitch(), deps.timeoutMs);
  }
  deps.navigate(exit === 'characterSelect' ? CHARACTER_SELECT_PATH : TITLE_PATH);
}
